import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import { useTranslation } from "react-i18next";
import { useOtpResendTimer } from "../../hooks/useOtpResendTimer";

interface OtpStepProps {
  identifier: string;
  otp: string;
  onOtpChange: (value: string) => void;
  onVerify: () => void;
  onResend: () => Promise<any>;
  isVerifying: boolean;
  error: string | null;
  extraFields?: React.ReactNode;
  submitLabel?: string;
}

const OtpStep: React.FC<OtpStepProps> = ({
  identifier,
  otp,
  onOtpChange,
  onVerify,
  onResend,
  isVerifying,
  error,
  extraFields,
  submitLabel,
}) => {
  const { t } = useTranslation();
  const { secondsLeft, canResend, start } = useOtpResendTimer();
  const [resending, setResending] = React.useState(false);

  React.useEffect(() => {
    start();

  }, []);

  const handleResend = async () => {
    if (!canResend || resending) return;
    setResending(true);
    try {
      await onResend();
      start();
    } finally {
      setResending(false);
    }
  };

  return (
    <Box
      component="form"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        onVerify();
      }}
      sx={{ width: "100%" }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {t("auth.otpSentTo")} <strong>{identifier}</strong>
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      <TextField
        margin="normal"
        required
        fullWidth
        label={t("auth.verificationCode")}
        value={otp}
        onChange={(e) => onOtpChange(e.target.value)}
        inputProps={{ maxLength: 6, inputMode: "numeric" }}
      />

      {extraFields}

      <Button type="submit" fullWidth variant="contained" disabled={isVerifying} sx={{ mt: 3, mb: 1 }}>
        {isVerifying ? <CircularProgress size={24} color="inherit" /> : submitLabel || t("auth.verify")}
      </Button>

      <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={handleResend}
          sx={{ opacity: canResend ? 1 : 0.5 }}
        >
          {canResend ? t("auth.resendCode") : t("auth.resendCodeIn", { seconds: secondsLeft })}
        </Link>
      </Box>
    </Box>
  );
};

export default OtpStep;
